import React from "react";
import {
  CANVAS_HEIGHT,
  CANVAS_WIDTH,
  WidescreenExportCanvas,
} from "../export/components/WidescreenExportCanvas";
import {
  buildArtifactRenderPayload,
  type ExportRenderValidationError,
  type WidescreenExportRenderPayload,
} from "../export/renderPayload";

type ArtifactExportState =
  | { status: "loading" }
  | { status: "ready"; payload: WidescreenExportRenderPayload }
  | { status: "error"; message: string };

export const ArtifactExportPage: React.FC = () => {
  const [state, setState] = React.useState<ArtifactExportState>({
    status: "loading",
  });

  React.useEffect(() => {
    let cancelled = false;

    async function loadPayload() {
      try {
        const params = new URLSearchParams(window.location.search);
        const reportUrl = params.get("report");
        const specUrl = params.get("spec");

        if (!reportUrl || !specUrl) {
          throw new Error(
            "Artifact export requires both report and spec query parameters.",
          );
        }

        const [report, spec] = await Promise.all([
          fetchJson(reportUrl, "report"),
          fetchJson(specUrl, "spec"),
        ]);
        const result = buildArtifactRenderPayload({ report, spec });

        if (!result.ok) {
          throw new Error(formatValidationErrors(result.errors));
        }

        if (!cancelled) {
          setState({ status: "ready", payload: result.payload });
        }
      } catch (error) {
        if (!cancelled) {
          setState({
            status: "error",
            message:
              error instanceof Error
                ? error.message
                : "Artifact export failed to load.",
          });
        }
      }
    }

    loadPayload();

    return () => {
      cancelled = true;
    };
  }, []);

  if (state.status === "error") {
    return (
      <div
        data-artifact-render-state="error"
        className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-10"
      >
        <div className="max-w-3xl rounded-lg border border-rose-400/40 bg-rose-950/40 p-8">
          <p className="text-sm uppercase tracking-[0.24em] text-rose-200">
            Artifact Render Error
          </p>
          <pre className="mt-4 whitespace-pre-wrap text-sm leading-6 text-rose-50">
            {state.message}
          </pre>
        </div>
      </div>
    );
  }

  if (state.status !== "ready") {
    return (
      <div
        data-artifact-render-state="loading"
        className="min-h-screen bg-slate-950 text-white flex items-center justify-center"
      >
        Loading artifact export...
      </div>
    );
  }

  const { data, template, callouts, metadata } = state.payload;

  return (
    <div
      data-artifact-render-state="ready"
      style={{ width: CANVAS_WIDTH, height: CANVAS_HEIGHT, overflow: "hidden" }}
    >
      <WidescreenExportCanvas
        data={data}
        template={template}
        callouts={callouts}
        metadata={metadata}
      />
    </div>
  );
};

async function fetchJson(url: string, label: string): Promise<unknown> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(
      `Unable to load ${label} JSON from ${url}: ${response.status} ${response.statusText}`,
    );
  }

  try {
    return await response.json();
  } catch (error) {
    throw new Error(
      `Unable to parse ${label} JSON from ${url}: ${
        error instanceof Error ? error.message : "invalid JSON"
      }`,
    );
  }
}

function formatValidationErrors(
  errors: ExportRenderValidationError[],
): string {
  return errors
    .map((error) => `${error.path} [${error.code}]: ${error.message}`)
    .join("\n");
}
